/**
 * @fileoverview Job Registry searches tab (ASIC / PPSR / land titles via
 * InfoTrack).
 *
 * URL: /dashboard/jobs/:jobId/searches/infotrack
 *
 * Single-page layout (no tabs):
 *   1. Manual search trigger (Registry → Product picker)
 *   2. Recommended searches awaiting practitioner approval
 *   3. Search history table — every order on the job (any status)
 *
 * Completed orders deep-link into the same `DocumentDetail` sheet used
 * across the gather phase, so practitioners stay on the searches page
 * while previewing imported PDFs.
 */

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Info, KeyRound } from "lucide-react";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Label } from "@/ui/primitives/label";
import { Switch } from "@/ui/primitives/switch";
import { SearchesRecommendedList } from "./-ui.searches-recommended-list";
import { SearchesHistoryTable } from "./-ui.searches-history-table";
import {
  SearchesManualTrigger,
  type ManualTriggerPrefill,
} from "./-ui.searches-manual-trigger";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/searches/infotrack",
)({
  component: JobInfotrackSearchesPage,
});

function JobInfotrackSearchesPage() {
  const { jobId } = Route.useParams();

  // InfoTrack is available to every job member (Beta). Searches require the
  // firm's own InfoTrack API key — see the key gate below.
  const { data: job } = useQuery({
    ...convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  });

  // InfoTrack searches require a per-firm API key. Without it every order
  // fails at submission, so gate the whole page behind a configured key and
  // point practitioners to Settings → Integrations.
  const { data: keyStatus } = useQuery({
    ...convexQueryOrIdle(
      api.settings.infotrack.queries.getInfotrackKeyStatus,
      job?.orgId ? { orgId: job.orgId } : undefined,
    ),
    enabled: !!job?.orgId,
  });

  const { data: recRows } = useQuery({
    ...convexQuery(api.jobs.searches.queries.listRecommendationsForJob, {
      jobId: jobId as Id<"jobs">,
    }),
  });

  const { data: orderRows } = useQuery({
    ...convexQuery(api.jobs.searches.queries.listOrdersForJob, {
      jobId: jobId as Id<"jobs">,
    }),
  });

  // KYC & AML orders live on their own tab — keep this history to the
  // ASIC / PPSR / LTO registries.
  const registryOrderRows = orderRows?.filter(
    (r) => r.registry !== "aml" && r.registry !== "infotrackid",
  );

  // Page-level prefill bus — recommendations push here when "Configure" is
  // clicked; the manual trigger reads from it and clears via `onPrefillConsumed`.
  const [manualPrefill, setManualPrefill] =
    useState<ManualTriggerPrefill | null>(null);

  // Page-level document sheet — opened by clicking "View document" on a
  // completed history row. Single instance shared by the whole table.
  const [selectedDocId, setSelectedDocId] = useState<Id<"documents"> | null>(
    null,
  );

  // "Show all candidates" toggle — when on, the Recommended section
  // additionally renders snoozed, rejected, and not-yet-suggested products
  // so practitioners can audit the full search universe for the job.
  const [showAllCandidates, setShowAllCandidates] = useState(false);

  const isViewer = job?.userRole === "viewer";

  // Gate the page until we know a key is configured. While the status query is
  // in flight (`keyStatus === undefined`) we render nothing to avoid flashing
  // the empty state before falling through to the searches UI.
  if (keyStatus && !keyStatus.configured) {
    return (
      <div className="flex flex-1 flex-col bg-muted p-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Registry searches
            </h1>
            <Badge color="violet" variant="soft" size="md">
              Beta
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Powered by InfoTrack. Manually run searches or run AI-recommended
            searches. All results will be added to the Job's Data.
          </p>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className={`${GROUP_CARD_CLASS} w-full max-w-md`}>
            <div className="flex flex-col items-center gap-4 px-8 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <KeyRound className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-lg font-semibold tracking-tight">
                  Configure your InfoTrack API key
                </h2>
                <p className="text-sm text-muted-foreground">
                  Your firm hasn't connected InfoTrack yet. Add your API key in
                  Settings → Integrations before you can run searches.
                </p>
              </div>
              <Button asChild>
                <Link to="/dashboard/settings/integrations">
                  Go to Settings → Integrations
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Registry searches
          </h1>
          <Badge color="violet" variant="soft" size="md">
            Beta
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Powered by InfoTrack. Manually run searches or run AI-recommended
          searches. All results will be added to the Job's Data.
        </p>
      </div>

      {/* Beta + billing notice */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-800 dark:bg-amber-950/40">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              InfoTrack searches are in beta
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Running a search will incur your existing InfoTrack rates per
              search. You'll be charged and billed directly by InfoTrack in your
              normal manner — Morgan does not add any charges on top.
            </p>
          </div>
        </div>
      </div>

      {/* Manual search */}
      <div className={GROUP_CARD_CLASS}>
        <div className="space-y-4 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Manual search
            </h2>
            <p className="text-sm text-muted-foreground">
              Manually select a registry search to run for the Job.
            </p>
          </div>
          <SearchesManualTrigger
            jobId={jobId as Id<"jobs">}
            prefill={manualPrefill ?? undefined}
            onPrefillConsumed={() => setManualPrefill(null)}
          />
        </div>
      </div>

      {/* Recommended */}
      <div className={GROUP_CARD_CLASS}>
        <div className="space-y-4 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                Recommended searches
              </h2>
              <p className="text-sm text-muted-foreground">
                Below is a list of the searches that Morgan AI recommends for
                the Job.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="show-all-candidates"
                checked={showAllCandidates}
                onCheckedChange={(v) => setShowAllCandidates(v === true)}
              />
              <Label
                htmlFor="show-all-candidates"
                className="cursor-pointer text-xs text-muted-foreground"
              >
                Show all searches
              </Label>
            </div>
          </div>
          {recRows ? (
            <SearchesRecommendedList
              jobId={jobId as Id<"jobs">}
              rows={recRows}
              showAllCandidates={showAllCandidates}
              onConfigure={(prefill) => setManualPrefill(prefill)}
            />
          ) : (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
        </div>
      </div>

      {/* Search history */}
      <h2 className="pt-4 text-lg font-semibold tracking-tight">
        Search history
      </h2>
      <p className="text-sm text-muted-foreground">
        Every order submitted to InfoTrack — in progress, completed, and failed.
      </p>
      <div className={GROUP_CARD_CLASS}>
        <div className="px-5 py-4">
          {registryOrderRows ? (
            <SearchesHistoryTable
              jobId={jobId as Id<"jobs">}
              rows={registryOrderRows}
              onOpenDocument={(id) => setSelectedDocId(id)}
            />
          ) : (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
        </div>
      </div>

      <DocumentDetail
        documentId={selectedDocId}
        open={selectedDocId !== null}
        isViewer={isViewer}
        onClose={() => setSelectedDocId(null)}
        defaultTab="original"
      />
    </div>
  );
}
